// src/pages/EmployeeLetter.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchEmployeeReport } from "../redux/slices/employeeReportSlice";
import "../assets/EmployeeLetter.css";

const EmployeeLetter = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { list: employees, loading, error, currentPage, totalPages } = useSelector(
    (state) => state.employeeReport
  );

  const superAdminId = localStorage.getItem("adminuserId") || "";

  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    employeeId: "",
    role: "",
    name: "",
    page: 1,
    pageSize: 10,
  });

  // Generate a color based on string (employee name)
  const getColorFromString = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const color = `hsl(${hash % 360}, 70%, 50%)`;
    return color;
  };

  const fetchData = (page = 1) => {
    if (!superAdminId) {
      alert("Admin ID not found in localStorage");
      return;
    }

    dispatch(
      fetchEmployeeReport({
        superAdminId,
        page,
        pageSize: filters.pageSize,
        filterType: filters.employeeId
          ? "userId"
          : filters.name
          ? "name"
          : filters.role
          ? "roleName"
          : undefined,
        filterValue: filters.employeeId || filters.name || filters.role || undefined,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
      })
    );
  };

  useEffect(() => {
    fetchData(1);
  }, []);


  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value, page: 1 }));
  };

  const handleSearch = () => fetchData(1);

  const handleIdCardClick = (user) => navigate("/EmployeeIdCard", { state: { user } });
  const handleJoiningLetterClick = (user) => navigate("/JoiningLetter", { state: { user } });
  const handlePromotionLetterClick = (user) => navigate("/PromotionLetter", { state: { user } });
  const handleExperienceLetterClick = (user) => navigate("/ExperienceLetter", { state: { user } });
  const handlePaySlipClick = (user) => navigate("/PaySlip", { state: { user } });
  const handleAgreementLetterClick = (user) => navigate("/AgreementLetter",{state: {user}});
  const handleOfferLetterClick = (user) => navigate("/OfferLetter",{state: {user}});

  const handlePageChange = (pageNumber) => {
    setFilters((prev) => ({ ...prev, page: pageNumber })); 
    fetchData(pageNumber);
  };

  return (
    <div className="EmployeeLetter-container">
      <h2 className="EmployeeLetter-title">Print Employee Letters</h2>

      {/* Filters */}
      <div className="EmployeeLetter-filterSection">
        <div className="EmployeeLetter-filterGroup">
          <label>From Date</label>
          <input type="date" name="fromDate" value={filters.fromDate} onChange={handleFilterChange} />
        </div>
        <div className="EmployeeLetter-filterGroup">
          <label>To Date</label>
          <input type="date" name="toDate" value={filters.toDate} onChange={handleFilterChange} />
        </div>
        <div className="EmployeeLetter-filterGroup">
          <label>Employee ID</label>
          <input type="text" name="employeeId" placeholder="Enter employee ID" value={filters.employeeId} onChange={handleFilterChange} />
        </div>
        <div className="EmployeeLetter-filterGroup">
          <label>Role</label>
          <select name="role" value={filters.role} onChange={handleFilterChange}>
            <option value="">-- Select Role --</option>
            {[...new Set(employees?.map((emp) => emp.roleName).filter(Boolean))].map((role, idx) => (
              <option key={idx} value={role}>{role}</option>
            ))}
          </select>
        </div>
        <div className="EmployeeLetter-filterGroup">
          <label>Employee Name</label>
          <input type="text" name="name" placeholder="Enter name" value={filters.name} onChange={handleFilterChange} />
        </div>
        <div className="EmployeeLetter-filterButtonWrapper">
          <button className="EmployeeLetter-searchButton" onClick={handleSearch}>Search</button>
        </div>
      </div>

      {/* Loading / Error */}
      {loading && <p>Loading employees...</p>}
      {error && <p style={{ color: "red" }}>{error}</p>}

      {/* Table */}
      <div className="EmployeeLetter-tableSection">
        <table className="EmployeeLetter-table">
          <thead>
            <tr>
              <th>SN</th>
              <th>Initials</th>
              <th>Employee ID</th>
              <th>Role</th>
              <th>Name</th>
              <th>Branch</th>
              <th>ID Card</th>
              <th>Joining Letter</th>
              <th>Promotion Letter</th>
              <th>Experience Letter</th>
              <th>Offer Letter</th>
              <th>Agreement Letter</th>
              {/* <th>Pay Slip</th> */}
            </tr>
          </thead>
          <tbody>
            {employees?.length > 0 ? (
              employees.map((emp, idx) => (
                <tr key={emp.id}>
                  <td>{(filters.page - 1) * filters.pageSize + idx + 1}</td>

                  {/* Initials with dynamic background */}
                  <td>
                    <div
                      style={{
                        width: "60px",
                        height: "60px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: getColorFromString(emp.fullName || emp.userId),
                        color: "#fff",
                        borderRadius: "50%",
                        fontWeight: "bold",
                        fontSize: "18px",
                      }}
                    >
                      {emp.fullName ? emp.fullName.substring(0, 2).toUpperCase() : "N/A"}
                    </div>
                  </td>

                  <td>{emp.userId||"N/A"}</td>
                  <td>{emp.roleName||"N/A"}</td>
                  <td>{emp.fullName||"N/A"}</td>
                  <td>{emp.branchName||"N/A"}</td>
                  <td>
                    <button className="EmployeeLetter-printBtn" onClick={() => handleIdCardClick(emp)}>Print</button>
                  </td>
                  <td>
                    <button className="EmployeeLetter-printBtn" onClick={() => handleJoiningLetterClick(emp)}>Print</button>
                  </td>
                  <td>
                    <button className="EmployeeLetter-printBtn" onClick={() => handlePromotionLetterClick(emp)}>Print</button>
                  </td>
                  <td>
                    <button className="EmployeeLetter-printBtn" onClick={() => handleExperienceLetterClick(emp)}>Print</button>
                  </td>
                  <td>
                    <button className="EmployeeLetter-printBtn" onClick={() => handleOfferLetterClick(emp)}>Print</button>
                  </td>
                  <td>
                    <button className="EmployeeLetter-printBtn" onClick={() => handleAgreementLetterClick(emp)}>Print</button>
                  </td>
                  {/* <td>
                    <button className="EmployeeLetter-printBtn" onClick={() => handlePaySlipClick(emp)}>Print</button>
                  </td> */}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="11" style={{ textAlign: "center" }}>No matching records found.</td>
              </tr>
            )}
          </tbody>
        </table>

      </div>
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="EmployeeLetter-pagination">
            <button onClick={() => handlePageChange(filters.page - 1)} disabled={filters.page === 1}>Prev</button>
             <span className="CustomerReport-pageIndicator">
            Page {filters.page} of {totalPages}
          </span>
            {/* {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                className={filters.page === i + 1 ? "active" : ""}
                onClick={() => handlePageChange(i + 1)}
              >
                {i + 1}
              </button>
            ))} */}
            <button onClick={() => handlePageChange(filters.page + 1)} disabled={filters.page === totalPages}>Next</button>
          </div>
        )}
    </div>
  );
};

export default EmployeeLetter;
